(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pageWrapper = document.querySelector('.page-wrapper');
  const finePointer = window.matchMedia('(pointer: fine)');
  let storySections = [];
  let collectionSections = [];
  let splitSections = [];
  let teamSections = [];
  let animationFrame = null;
  let smoothScrollFrame = null;
  let smoothScrollPosition = 0;
  let smoothScrollTarget = 0;
  const getMenuCloseFallbackDuration = () => (reducedMotion.matches ? 0 : 900);

  const syncMenuState = () => {
    document.documentElement.classList.toggle(
      'fh-menu-open',
      Boolean(document.querySelector('[data-fh-menu-drawer][open]'))
    );
  };

  const closeMenu = (drawer, returnFocus = true) => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;

    drawer.classList.add('is-closing');
    drawer.classList.remove('is-active');
    document.documentElement.classList.add('fh-menu-closing');

    let fallbackTimer = null;
    const animatedPanel = drawer.querySelector('.fh-nav__drawer-inner');
    function handleCloseTransitionEnd(event) {
      if (event.target !== animatedPanel || event.propertyName !== 'transform') return;
      finishClose();
    }
    const finishClose = () => {
      if (!drawer.classList.contains('is-closing')) return;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      animatedPanel?.removeEventListener('transitionend', handleCloseTransitionEnd);
      drawer.open = false;
      drawer.classList.remove('is-closing');
      document.documentElement.classList.remove('fh-menu-closing');
      syncMenuState();
      if (returnFocus) drawer.querySelector('.fh-nav__control--menu')?.focus();
    };

    if (reducedMotion.matches || !animatedPanel) {
      finishClose();
      return;
    }

    animatedPanel.addEventListener('transitionend', handleCloseTransitionEnd);
    fallbackTimer = window.setTimeout(finishClose, getMenuCloseFallbackDuration());
  };

  const initializeMenus = (scope = document) => {
    scope.querySelectorAll('[data-fh-menu-drawer]').forEach((drawer) => {
      if (drawer.dataset.fhMenuInitialized === 'true') return;

      drawer.dataset.fhMenuInitialized = 'true';
      const trigger = drawer.querySelector('.fh-nav__control--menu');

      trigger?.addEventListener('click', (event) => {
        if (!drawer.open) return;
        event.preventDefault();
        closeMenu(drawer);
      });

      drawer.addEventListener('toggle', () => {
        if (!drawer.open) {
          drawer.classList.remove('is-active', 'is-closing');
          document.documentElement.classList.remove('fh-menu-closing');
          syncMenuState();
          return;
        }

        syncMenuState();
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => drawer.classList.add('is-active'));
        });
      });
    });

    syncMenuState();
  };

  const formatSliderNumber = (number) => String(number).padStart(2, '0');

  const initializeMobileSliders = (scope = document) => {
    scope.querySelectorAll('[data-fh-mobile-slider]').forEach((slider) => {
      if (slider.dataset.fhSliderInitialized === 'true') return;

      const section = slider.closest('.fh-section');
      const controls = section?.querySelector('[data-fh-slider-controls]');
      const previousButton = controls?.querySelector('[data-fh-slider-previous]');
      const nextButton = controls?.querySelector('[data-fh-slider-next]');
      const currentLabel = controls?.querySelector('[data-fh-slider-current]');
      const totalLabel = controls?.querySelector('[data-fh-slider-total]');
      const items = Array.from(slider.children);
      const displayedTotal = Number.parseInt(controls?.dataset.fhSliderTotalCount, 10) || items.length;

      if (!controls || items.length === 0) return;

      slider.dataset.fhSliderInitialized = 'true';
      let currentIndex = 0;
      let scrollFrame = null;
      let navigationTimer = null;
      let isNavigating = false;

      const getSlideOffset = (index) => {
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        if (items.length === 1) return 0;
        return (maxScroll * index) / (items.length - 1);
      };

      const renderCounter = () => {
        if (currentLabel) currentLabel.textContent = formatSliderNumber(currentIndex + 1);
        if (totalLabel) totalLabel.textContent = formatSliderNumber(displayedTotal);
        previousButton?.setAttribute('aria-disabled', String(currentIndex === 0));
        nextButton?.setAttribute('aria-disabled', String(currentIndex === items.length - 1));
      };

      const findClosestIndex = () => {
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);

        if (slider.scrollLeft <= 1) return 0;
        if (slider.scrollLeft >= maxScroll - 1) return items.length - 1;

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        items.forEach((_, index) => {
          const distance = Math.abs(getSlideOffset(index) - slider.scrollLeft);
          if (distance >= closestDistance) return;
          closestDistance = distance;
          closestIndex = index;
        });

        return closestIndex;
      };

      const goToSlide = (index) => {
        currentIndex = Math.max(0, Math.min(items.length - 1, index));
        isNavigating = true;
        renderCounter();
        slider.scrollTo({
          left: getSlideOffset(currentIndex),
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });

        if (navigationTimer !== null) window.clearTimeout(navigationTimer);
        navigationTimer = window.setTimeout(
          () => {
            navigationTimer = null;
            isNavigating = false;
            currentIndex = findClosestIndex();
            renderCounter();
          },
          reducedMotion.matches ? 0 : 550
        );
      };

      previousButton?.addEventListener('click', (event) => {
        event.preventDefault();
        goToSlide(currentIndex - 1);
      });
      nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        goToSlide(currentIndex + 1);
      });

      slider.addEventListener(
        'scroll',
        () => {
          if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(() => {
            scrollFrame = null;
            if (isNavigating) return;
            const nextIndex = findClosestIndex();
            if (nextIndex === currentIndex) return;
            currentIndex = nextIndex;
            renderCounter();
          });
        },
        { passive: true }
      );

      renderCounter();
    });
  };

  const getScrollRoot = () => {
    if (window.innerWidth >= 990 && pageWrapper) return pageWrapper;
    return document.scrollingElement;
  };

  const canNestedElementScroll = (target, delta) => {
    let element = target instanceof Element ? target : target?.parentElement;

    while (element && element !== pageWrapper && element !== document.body) {
      const styles = window.getComputedStyle(element);
      const canScroll = /(auto|scroll)/.test(styles.overflowY) && element.scrollHeight > element.clientHeight;

      if (canScroll) {
        const hasRoomBelow = element.scrollTop + element.clientHeight < element.scrollHeight - 1;
        const hasRoomAbove = element.scrollTop > 1;

        if ((delta > 0 && hasRoomBelow) || (delta < 0 && hasRoomAbove)) return true;
      }

      element = element.parentElement;
    }

    return false;
  };

  const stopSmoothScroll = () => {
    if (smoothScrollFrame !== null) {
      window.cancelAnimationFrame(smoothScrollFrame);
      smoothScrollFrame = null;
    }

    pageWrapper?.classList.remove('fh-smooth-scroll-active');
    document.documentElement.classList.remove('fh-smooth-scroll-active');
  };

  const animateSmoothScroll = () => {
    const scrollRoot = getScrollRoot();

    if (!scrollRoot) {
      stopSmoothScroll();
      return;
    }

    const distance = smoothScrollTarget - smoothScrollPosition;
    smoothScrollPosition += distance * 0.14;
    scrollRoot.scrollTop = smoothScrollPosition;

    if (Math.abs(distance) < 0.5) {
      scrollRoot.scrollTop = smoothScrollTarget;
      smoothScrollPosition = smoothScrollTarget;
      stopSmoothScroll();
      return;
    }

    smoothScrollFrame = window.requestAnimationFrame(animateSmoothScroll);
  };

  const handleSmoothWheel = (event) => {
    if (reducedMotion.matches || !finePointer.matches || event.ctrlKey) return;

    const scrollRoot = getScrollRoot();
    if (!scrollRoot || canNestedElementScroll(event.target, event.deltaY)) return;

    event.preventDefault();

    const deltaMultiplier =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 20
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? window.innerHeight
          : 1;
    const maxScroll = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);

    if (smoothScrollFrame === null) {
      smoothScrollPosition = scrollRoot.scrollTop;
      smoothScrollTarget = smoothScrollPosition;
      scrollRoot.classList.add('fh-smooth-scroll-active');
    }

    smoothScrollTarget = Math.min(
      maxScroll,
      Math.max(0, smoothScrollTarget + event.deltaY * deltaMultiplier)
    );

    if (smoothScrollFrame === null) {
      smoothScrollFrame = window.requestAnimationFrame(animateSmoothScroll);
    }
  };

  const resetStory = (section) => {
    section
      .querySelectorAll('.fh-story__image')
      .forEach((image) => image.style.removeProperty('--fh-story-parallax-y'));
  };

  const resetCollection = (section) => {
    section
      .querySelectorAll('[data-fh-collection-card]')
      .forEach((card) => card.style.removeProperty('--fh-collection-parallax-y'));
  };

  const resetSplit = (section) => {
    section
      .querySelector(':is(.fh-split__detail, .fh-parallax-detail)')
      ?.style.removeProperty('--fh-split-detail-parallax-y');
  };

  const resetTeam = (section) => {
    section
      .querySelectorAll('[data-fab-team-card]')
      .forEach((card) => card.style.removeProperty('--fab-team-parallax-y'));
  };

  const updateParallax = () => {
    animationFrame = null;

    if (reducedMotion.matches) {
      storySections.forEach(resetStory);
      collectionSections.forEach(resetCollection);
      splitSections.forEach(resetSplit);
      teamSections.forEach(resetTeam);
      return;
    }

    const viewportHeight = window.innerHeight;

    storySections.forEach((section) => {
      const bounds = section.getBoundingClientRect();

      if (bounds.bottom < 0 || bounds.top > viewportHeight) return;

      const progress = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
      const centeredProgress = Math.min(1, Math.max(0, progress)) - 0.5;
      const primaryImage = section.querySelector('.fh-story__image--primary');
      const detailImage = section.querySelector('.fh-story__image--detail');
      const isMobile = window.innerWidth < 750;
      // The left detail moves away from the copy as the page scrolls down.
      // Layout gutters reserve the complete travel, including its starting offset.
      const primaryMovement = isMobile ? -40 : -300;
      const detailMovement = isMobile ? 30 : 200;

      primaryImage?.style.setProperty('--fh-story-parallax-y', `${centeredProgress * primaryMovement}px`);
      detailImage?.style.setProperty('--fh-story-parallax-y', `${centeredProgress * detailMovement}px`);
    });

    collectionSections.forEach((section) => {
      const bounds = section.getBoundingClientRect();

      if (bounds.bottom < 0 || bounds.top > viewportHeight) return;

      const progress = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
      const centeredProgress = Math.min(1, Math.max(0, progress)) - 0.5;
      const movement = window.innerWidth < 750 ? 48 : 120;

      section.querySelectorAll('[data-fh-collection-card]').forEach((card) => {
        const direction = card.dataset.parallaxDirection === 'up' ? -1 : 1;
        card.style.setProperty(
          '--fh-collection-parallax-y',
          `${centeredProgress * movement * direction}px`
        );
      });
    });

    splitSections.forEach((section) => {
      const bounds = section.getBoundingClientRect();

      if (bounds.bottom < 0 || bounds.top > viewportHeight) return;

      const progress = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
      const centeredProgress = Math.min(1, Math.max(0, progress)) - 0.5;
      const movement = window.innerWidth < 750 ? 60 : 120;
      const direction = section.dataset.detailParallaxDirection === 'up' ? -1 : 1;
      const detailImage = section.querySelector(':is(.fh-split__detail, .fh-parallax-detail)');

      detailImage?.style.setProperty(
        '--fh-split-detail-parallax-y',
        `${centeredProgress * movement * direction}px`
      );
    });

    teamSections.forEach((section) => {
      const bounds = section.getBoundingClientRect();

      if (bounds.bottom < 0 || bounds.top > viewportHeight) return;

      const progress = (viewportHeight - bounds.top) / (viewportHeight + bounds.height);
      const centeredProgress = Math.min(1, Math.max(0, progress)) - 0.5;
      const movement = window.innerWidth < 750 ? 32 : 64;

      section.querySelectorAll('[data-fab-team-card]').forEach((card, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        card.style.setProperty(
          '--fab-team-parallax-y',
          `${centeredProgress * movement * direction}px`
        );
      });
    });
  };

  const requestUpdate = () => {
    if (animationFrame !== null) return;
    animationFrame = window.requestAnimationFrame(updateParallax);
  };

  const findParallaxSections = () => {
    storySections = Array.from(document.querySelectorAll('[data-fh-story-parallax]'));
    collectionSections = Array.from(document.querySelectorAll('[data-fh-collection-parallax]'));
    splitSections = Array.from(document.querySelectorAll('[data-fh-split-parallax]'));
    teamSections = Array.from(document.querySelectorAll('[data-fab-team]'));
    requestUpdate();
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  pageWrapper?.addEventListener('scroll', requestUpdate, { passive: true });
  pageWrapper?.addEventListener('wheel', handleSmoothWheel, { passive: false });
  window.addEventListener('resize', requestUpdate);
  window.addEventListener('resize', stopSmoothScroll);
  reducedMotion.addEventListener('change', requestUpdate);
  reducedMotion.addEventListener('change', stopSmoothScroll);
  document.addEventListener('shopify:section:load', findParallaxSections);
  document.addEventListener('shopify:section:unload', findParallaxSections);
  document.addEventListener('shopify:section:load', (event) => initializeMenus(event.target));
  document.addEventListener('shopify:section:load', (event) => initializeMobileSliders(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openDrawer = document.querySelector('[data-fh-menu-drawer][open]');
    if (openDrawer) {
      event.preventDefault();
      closeMenu(openDrawer);
    }
  });

  initializeMenus();
  initializeMobileSliders();
  findParallaxSections();
})();
